import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor, registerPlugin } from "@capacitor/core";

const API_ORIGIN = "https://focus.aiqel.io";
const TOKEN_KEY = "aiqel_session";

type NativePlugin = {
  secureGet(options: { key: string }): Promise<{ value: string | null }>;
  secureSet(options: { key: string; value: string }): Promise<void>;
  secureRemove(options: { key: string }): Promise<void>;
  signInWithApple(): Promise<{ identityToken: string; name?: string }>;
  setKeepAwake(options: { enabled: boolean }): Promise<void>;
  updateWidget(options: { snapshot: string }): Promise<void>;
};

const AIqelNative = registerPlugin<NativePlugin>("AIqelNative");

export const isNative = Capacitor.isNativePlatform();

export async function nativeToken() {
  if (!isNative) return null;
  return (await AIqelNative.secureGet({ key: TOKEN_KEY })).value;
}

export async function nativeFetch(path: string, init?: RequestInit) {
  const token = await nativeToken();
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined && !headers.has("content-type"))
    headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  return fetch(isNative ? new URL(path, API_ORIGIN) : path, {
    credentials: "include",
    ...init,
    headers,
  });
}

export async function openNativeGoogleLogin() {
  if (!isNative) {
    window.location.assign("/api/auth/google");
    return;
  }
  await Browser.open({
    url: `${API_ORIGIN}/api/auth/google?platform=ios`,
    presentationStyle: "popover",
  });
}

export async function openNativeAppleLogin() {
  if (!isNative) return;
  const credential = await AIqelNative.signInWithApple();
  const response = await fetch(`${API_ORIGIN}/api/auth/apple`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(credential),
  });
  if (!response.ok)
    throw new Error("Sign in with Apple could not be completed");
  const payload = (await response.json()) as { token: string };
  await AIqelNative.secureSet({ key: TOKEN_KEY, value: payload.token });
  window.location.replace("/");
}

async function exchangeMobileCode(url: string) {
  const callback = new URL(url);
  if (callback.protocol !== "aiqelfocus:" || callback.hostname !== "auth")
    return;
  const code = callback.searchParams.get("code");
  if (!code) return;
  const response = await fetch(`${API_ORIGIN}/api/auth/mobile/exchange`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error("Mobile sign-in could not be completed");
  const payload = (await response.json()) as { token: string };
  await AIqelNative.secureSet({ key: TOKEN_KEY, value: payload.token });
  await Browser.close().catch(() => undefined);
  window.location.replace("/");
}

export async function initializeNativeApp() {
  if (!isNative) return;
  await AIqelNative.setKeepAwake({ enabled: true });
  await App.addListener("appUrlOpen", ({ url }) => {
    void exchangeMobileCode(url);
  });
  const launch = await App.getLaunchUrl();
  if (launch?.url) await exchangeMobileCode(launch.url);
}

export async function clearNativeSession() {
  if (isNative) await AIqelNative.secureRemove({ key: TOKEN_KEY });
}

export async function updateNativeWidget(snapshot: unknown) {
  if (!isNative) return;
  await AIqelNative.updateWidget({ snapshot: JSON.stringify(snapshot) });
}
